import { useMemo } from 'react';

import { RetailerNetworkLocationFilter, RetailerNetworkLocationFragment } from '@occc/graphql';

import { useGetRetailerNetworkLocations } from '../../../data-access/queries';
import {
  ConnectionStatus,
  toConnectionStatusEquals,
} from '../retailer-network-my-stores-v2-page.types';

type UseMyStoresLocationsOptions = {
  networkId: string;
  stateFilter: string;
  cityFilter: string;
  connectionStatusFilter: ConnectionStatus | '';
  search: string;
  selectedLocationUuid: string | null;
};

type UseMyStoresLocationsResult = {
  locations: RetailerNetworkLocationFragment[];
  mapMarkerLocations: RetailerNetworkLocationFragment[];
  mapBoundsLocations: RetailerNetworkLocationFragment[];
};

// Fetches locations for map pin rendering, filtered server-side.
// retailerNetworkLocationCoordinates is deployed but requires mandatory city/state filters,
// which don't apply here (State/City are optional on this page) — keep using
// retailerNetworkLocations, matching the grouped/flat store lists' own filtering.
const ALL_LOCATIONS_PAGE_SIZE = 500;

export const useStoresLocations = ({
  networkId,
  stateFilter,
  cityFilter,
  connectionStatusFilter,
  search,
  selectedLocationUuid,
}: UseMyStoresLocationsOptions): UseMyStoresLocationsResult => {
  const filter: RetailerNetworkLocationFilter = {
    retailerNetworkUuidEquals: networkId,
    stateEquals: stateFilter || undefined,
    cityEquals: cityFilter || undefined,
    connectionStatusEquals: toConnectionStatusEquals(connectionStatusFilter),
    search: search.trim() || undefined,
  };

  const { data: locations = [] } = useGetRetailerNetworkLocations({
    filter,
    pageSize: ALL_LOCATIONS_PAGE_SIZE,
  });

  // The server already returns the narrowed set — no client-side filtering needed.
  const mapMarkerLocations = locations;

  // When a pin is selected, narrow the bounds to that pin; otherwise use the full filtered set.
  const mapBoundsLocations = useMemo(() => {
    if (selectedLocationUuid) {
      const selected = mapMarkerLocations.filter(
        (l: RetailerNetworkLocationFragment) => l.uuid === selectedLocationUuid,
      );
      if (selected.length > 0) return selected;
    }
    return mapMarkerLocations;
  }, [mapMarkerLocations, selectedLocationUuid]);

  return { locations, mapMarkerLocations, mapBoundsLocations };
};
